import styled, { keyframes } from 'styled-components/macro';
import { useTranslation } from 'react-i18next';
import { BasketBarSelectedProducts_selectedProducts } from './__generated__/BasketBarSelectedProducts';

const slideUp = keyframes`
  from { transform: translateY(100%); }
  to   { transform: translateY(0); }
`;

const fadeIn = keyframes`
  from { opacity: 0; }
  to   { opacity: 1; }
`;

const PickerOverlay = styled.div`
  position: absolute;
  inset: 0;
  background: rgba(0, 0, 0, 0.4);
  z-index: 25000;
  animation: ${fadeIn} 0.2s ease;
`;

const PickerSheet = styled.div`
  position: absolute;
  bottom: 0;
  left: 0;
  right: 0;
  background: #fff;
  border-radius: 16px 16px 0 0;
  z-index: 25001;
  padding: 2rem 1.6rem 3.2rem;
  display: flex;
  flex-direction: column;
  gap: 1.6rem;
  animation: ${slideUp} 0.25s cubic-bezier(0.25, 0.46, 0.45, 0.94);
`;

const PickerHandle = styled.div`
  width: 3.6rem;
  height: 4px;
  background: #e0e0e0;
  border-radius: 2px;
  margin: 0 auto -0.8rem;
`;

const PickerProductName = styled.div`
  font-family: var(--font-primary);
  font-size: 1.4rem;
  font-weight: 600;
  color: #000;
  text-align: center;
`;

const PickerSubtitle = styled.div`
  font-family: var(--font-primary);
  font-size: 1.2rem;
  font-weight: 400;
  color: #777;
  text-align: center;
  margin-top: -1rem;
`;

const SizeGrid = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 0.8rem;
`;

const SizeChip = styled.button<{ $selected: boolean; $adding: boolean }>`
  flex: 0 0 auto;
  min-width: 5.6rem;
  padding: 0.8rem 1.2rem;
  border-radius: 6px;
  border: 1.5px solid ${({ $selected }) => ($selected ? '#000' : '#d0d0d0')};
  background: #fff;
  color: #000;
  font-family: var(--font-primary);
  font-size: 1.3rem;
  font-weight: 500;
  cursor: ${({ $adding }) => ($adding ? 'not-allowed' : 'pointer')};
  opacity: ${({ $adding }) => ($adding ? 0.6 : 1)};
  display: flex;
  align-items: center;
  justify-content: center;
  transition: background 0.15s, border-color 0.15s, color 0.15s;

  &:hover:not(:disabled) {
    border-color: #000;
  }
`;

const ConfirmButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 100%;
  padding: 1.6rem;
  background: var(--design-base-color, #000);
  color: #fff;
  font-family: var(--font-primary);
  font-size: 1.4rem;
  font-weight: 700;
  text-transform: uppercase;
  border: none;
  border-radius: 25px;
  cursor: pointer;
  opacity: ${({ disabled }) => (disabled ? 0.5 : 1)};

  &:hover:not(:disabled) {
    opacity: 0.9;
  }
`;

type Props = {
  product: BasketBarSelectedProducts_selectedProducts | null;
  selectedSize: string;
  onSelectSize: (size: string) => void;
  onClose: () => void;
  onConfirm: () => void;
  isAdding: boolean;
};

export default function BasketBarCrossSellPicker({
  product,
  selectedSize,
  onSelectSize,
  onClose,
  onConfirm,
  isAdding,
}: Props) {
  const { t } = useTranslation('shipping', { useSuspense: false });

  if (!product) return null;

  const sizes = (product.sizeDatas ?? []).filter((s) => (s.stock ?? 0) > 0);

  return (
    <>
      <PickerOverlay onClick={onClose} />
      <PickerSheet>
        <PickerHandle />
        <PickerProductName>
          {product.brandDatas?.name} — {product.title}
        </PickerProductName>
        <PickerSubtitle>{t('chooseSize')}</PickerSubtitle>
        <SizeGrid>
          {sizes.map((s) => (
            <SizeChip
              key={s.productId}
              $selected={selectedSize === s.size}
              $adding={isAdding}
              onClick={() => {
                if (isAdding) return;
                onSelectSize(s.size);
              }}
            >
              {s.size}
            </SizeChip>
          ))}
        </SizeGrid>
        <ConfirmButton onClick={onConfirm} disabled={!selectedSize || isAdding}>
          {isAdding ? '...' : t('addToBasket')}
        </ConfirmButton>
      </PickerSheet>
    </>
  );
}
